import React, { useRef, useState } from "react";
import { Card } from "./shared";
import { PB_SLIDES } from "./Pitchbook";
import { ChevronRight, Trash2 } from "lucide-react";

const MAX = 3;

// Up to three deck-only cover photos; blank means the deck reuses the room's platform banner.
function DeckCover({ value, onChange, fallback = [] }) {
  const input = useRef(null);
  const list = (value || "").split("|").filter(Boolean);
  const take = (files) => {
    const imgs = Array.from(files || []).filter((f) => f.type.startsWith("image/")).slice(0, MAX - list.length);
    if (!imgs.length) return;
    Promise.all(imgs.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }))).then((urls) => onChange([...list, ...urls].slice(0, MAX).join("|")));
  };
  const shown = list.length ? list : fallback.slice(0, MAX);
  return (
    <div className="field" data-testid="pb-cover-field">
      <label>Cover photos</label>
      <div className="ra-sub" style={{ marginBottom: 6 }}>
        {list.length ? `${list.length} deck photo${list.length > 1 ? "s" : ""} — these override the room banner.`
          : fallback.length ? `Reusing ${fallback.length} photo${fallback.length > 1 ? "s" : ""} from the deal room's platform banner. Upload here to use different ones.`
            : "No photos yet — the cover prints on the dark gradient. Upload up to 3 wide shots."}
      </div>
      {shown.length > 0 && (
        <div className="ed-photo-row" data-testid="pb-cover-list">
          {shown.map((src, i) => (
            <div className="ed-photo-item" key={src + i} data-testid={`pb-cover-item-${i}`}>
              <img src={src} alt="" className="ed-photo-preview" />
              {list.length > 0 && <button className="icon-btn" title="Remove" onClick={() => onChange(list.filter((_, n) => n !== i).join("|"))} data-testid={`pb-cover-remove-${i}`}><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <label className="btn btn-sm" style={{ cursor: "pointer" }} data-testid="pb-cover-btn">
          {list.length >= MAX ? "3 photos added" : "Upload deck cover"}
          <input ref={input} type="file" accept="image/*" multiple hidden disabled={list.length >= MAX} onChange={(e) => take(e.target.files)} data-testid="pb-cover-input" />
        </label>
        {list.length > 0 && <button className="btn btn-sm" onClick={() => onChange("")} data-testid="pb-cover-reset">Back to room photos</button>}
      </div>
      <input className="inp" style={{ marginTop: 8 }} value="" placeholder="…or paste an image URL"
        disabled={list.length >= MAX} onChange={(e) => e.target.value && onChange([...list, e.target.value].slice(0, MAX).join("|"))} data-testid="pb-cover-url" />
    </div>
  );
}

export function PitchbookConfig({ secs, setSecs, ov, setOv, cover, setCover, fallback, blActive }) {
  const [open, setOpen] = useState("");
  const count = PB_SLIDES.filter((s) => secs[s.id] && (s.id !== "backlever" || blActive)).length;
  const setOne = (id, patch) => setOv({ ...ov, [id]: { ...(ov[id] || {}), ...patch } });
  return (
    <>
      <Card title={`Slides to include (${count})`} testid="pb-slides">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {PB_SLIDES.map((s) => {
            const off = s.id === "backlever" && !blActive;
            const edited = ov[s.id] && (ov[s.id].h || ov[s.id].b);
            return (
              <div key={s.id} data-testid={`pb-row-${s.id}`}>
                <div className="cim-sec-row" style={{ opacity: off ? 0.45 : 1 }}>
                  <input type="checkbox" checked={!!secs[s.id] && !off} disabled={off}
                    onChange={() => !off && setSecs({ ...secs, [s.id]: !secs[s.id] })} data-testid={`pb-chk-${s.id}`} />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: "var(--text)" }}>{s.label}{edited ? " ·" : ""}</span>
                    <span style={{ display: "block", fontSize: 10.5, color: "var(--text3)" }}>{off ? "Turn the back-lever on in Assumptions to include this" : s.desc}</span>
                  </span>
                  {s.id !== "toc" && (
                    <button className="btn btn-sm" onClick={() => setOpen(open === s.id ? "" : s.id)} data-testid={`pb-edit-${s.id}`}>
                      <ChevronRight size={11} style={{ marginRight: 4, transform: open === s.id ? "rotate(90deg)" : "none", transition: "transform .15s" }} />Text
                    </button>
                  )}
                </div>
                {open === s.id && (
                  <div style={{ padding: "6px 0 12px 26px" }} data-testid={`pb-ov-${s.id}`}>
                    <input className="inp" value={(ov[s.id] || {}).h || ""} onChange={(e) => setOne(s.id, { h: e.target.value })}
                      placeholder="Headline — leave blank to use the auto-written line" data-testid={`pb-ov-h-${s.id}`} />
                    <textarea className="inp" rows={3} style={{ marginTop: 6 }} value={(ov[s.id] || {}).b || ""} onChange={(e) => setOne(s.id, { b: e.target.value })}
                      placeholder="Body copy / footnote — blank keeps the model-written paragraph" data-testid={`pb-ov-b-${s.id}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
      <Card title="Cover" style={{ marginTop: 12 }} testid="pb-cover-card">
        <DeckCover value={cover} onChange={setCover} fallback={fallback} />
      </Card>
    </>
  );
}
